import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, Pressable, Image } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WORLD_CUP_PAYOUT_MODE } from '../../../src/worldcup/worldcup.constants';
import type { WorldCupPayoutMode } from '../../../src/worldcup/worldcup.constants';
import {
  CREATE_WORLD_CUP_GROUP_COPY,
  COIN_SLIDER_STEPS,
} from './CreateWorldCupGroupScreen.constants';
import { getCreateWorldCupGroupStep2FormStyles } from './CreateWorldCupGroupStep2Form.styles';

export type CreateWorldCupGroupStep2FormProps = {
  theme: Theme;
  entryFeeCoins: number;
  onEntryFeeCoins: (n: number) => void;
  payoutMode: WorldCupPayoutMode;
  onPayoutMode: (mode: WorldCupPayoutMode) => void;
  pctFirst: string;
  pctSecond: string;
  pctThird: string;
  onPctFirst: (t: string) => void;
  onPctSecond: (t: string) => void;
  onPctThird: (t: string) => void;
  isSubmitting: boolean;
  showPctError: boolean;
};

const POT_BAR_SECONDARY_COLOR = 'rgba(167,139,250,0.5)';
const POT_BAR_SECONDARY_LABEL_COLOR = 'rgba(167,139,250,0.7)';

type PotBarsProps = {
  bars: readonly number[];
  accentColor: string;
  styles: ReturnType<typeof getCreateWorldCupGroupStep2FormStyles>;
};

function PotBars({ bars, accentColor, styles }: PotBarsProps) {
  return (
    <View style={styles.potBarsContainer}>
      {bars.map((pct, i) => {
        const barColor = i === 0 ? accentColor : POT_BAR_SECONDARY_COLOR;
        const labelColor = i === 0 ? accentColor : POT_BAR_SECONDARY_LABEL_COLOR;
        const barHeight = Math.max(Math.round(pct * 0.26), 4);
        return (
          <View key={i} style={styles.potBarsItemWrap}>
            <View
              style={[
                styles.potBarsBar,
                { height: barHeight, backgroundColor: barColor },
              ]}
            />
            <Text style={[styles.potBarsLabel, { color: labelColor }]}>
              {pct}%
            </Text>
          </View>
        );
      })}
    </View>
  );
}

const WTA_BARS = [100] as const;
const TOP3_BARS = [60, 30, 10] as const;

export const CreateWorldCupGroupStep2Form: React.FC<CreateWorldCupGroupStep2FormProps> = ({
  theme,
  entryFeeCoins,
  onEntryFeeCoins,
  payoutMode,
  onPayoutMode,
  pctFirst,
  pctSecond,
  pctThird,
  onPctFirst,
  onPctSecond,
  onPctThird,
  isSubmitting,
  showPctError,
}) => {
  const styles = useMemo(() => getCreateWorldCupGroupStep2FormStyles(theme), [theme]);
  const [usingCustom, setUsingCustom] = useState(false);
  const [customText, setCustomText] = useState('');

  const isWTA = payoutMode === WORLD_CUP_PAYOUT_MODE.WINNER_TAKE_ALL;
  const isTop3 = payoutMode === WORLD_CUP_PAYOUT_MODE.TOP_THREE;

  const handlePresetTap = (v: number) => {
    setUsingCustom(false);
    setCustomText('');
    onEntryFeeCoins(v);
  };

  const handleCustomChange = (text: string) => {
    const digits = text.replace(/\D/g, '');
    setUsingCustom(true);
    setCustomText(digits);
    const n = parseInt(digits, 10);
    onEntryFeeCoins(isNaN(n) ? 0 : Math.min(n, 99999));
  };

  const isPresetActive = (v: number) => !usingCustom && entryFeeCoins === v;

  return (
    <View style={styles.root}>
      {/* Buy-in presets */}
      <Text style={styles.sectionLabel}>{CREATE_WORLD_CUP_GROUP_COPY.SECTION_BUY_IN}</Text>
      <View style={styles.chipGrid}>
        {COIN_SLIDER_STEPS.map((v) => {
          const active = isPresetActive(v);
          return (
            <Pressable
              key={v}
              style={[styles.chip, active && styles.chipActive]}
              onPress={() => handlePresetTap(v)}
              disabled={isSubmitting}
              testID={`create-world-cup-group-buyin-${v}`}
            >
              {v === 0 ? (
                <Text style={styles.chipFreeText}>{CREATE_WORLD_CUP_GROUP_COPY.FREE_LABEL}</Text>
              ) : (
                <View style={styles.chipCoinRow}>
                  <Image source={IDLE_COIN_SOURCE} style={styles.chipCoinIcon} />
                  <Text style={[styles.chipText, active && styles.chipTextActive]}>
                    {v >= 1000 ? '1K' : String(v)}
                  </Text>
                </View>
              )}
            </Pressable>
          );
        })}
      </View>
      <View style={styles.customRow}>
        <Text style={styles.customOrText}>or</Text>
        <View style={styles.customInputWrap}>
          <Image source={IDLE_COIN_SOURCE} style={styles.customCoinIcon} />
          <TextInput
            value={customText}
            onChangeText={handleCustomChange}
            placeholder="Custom amount"
            placeholderTextColor={theme.gray[400]}
            style={styles.customInput}
            keyboardType="number-pad"
            editable={!isSubmitting}
            testID="create-world-cup-group-buyin-custom"
          />
        </View>
      </View>

      {/* Pot split */}
      <Text style={styles.sectionLabel}>{CREATE_WORLD_CUP_GROUP_COPY.SECTION_POT_SPLIT}</Text>
      <Pressable
        style={[styles.potSplitCard, isWTA && styles.potSplitCardActive]}
        onPress={() => onPayoutMode(WORLD_CUP_PAYOUT_MODE.WINNER_TAKE_ALL)}
        disabled={isSubmitting}
        testID="create-world-cup-group-payout-wta"
      >
        <View style={styles.potSplitBarsWrap}>
          <PotBars bars={WTA_BARS} accentColor={theme.accent} styles={styles} />
        </View>
        <View style={styles.potSplitCardBody}>
          <Text style={styles.potSplitCardName}>{CREATE_WORLD_CUP_GROUP_COPY.PAYOUT_WINNER_TAKE_ALL}</Text>
          <Text style={styles.potSplitCardDesc}>{CREATE_WORLD_CUP_GROUP_COPY.PAYOUT_WINNER_TAKE_ALL_DESC}</Text>
        </View>
        <View style={[styles.potSplitRadio, isWTA && styles.potSplitRadioActive]}>
          {isWTA && <View style={styles.potSplitRadioDot} />}
        </View>
      </Pressable>

      <Pressable
        style={[styles.potSplitCard, isTop3 && styles.potSplitCardActive]}
        onPress={() => onPayoutMode(WORLD_CUP_PAYOUT_MODE.TOP_THREE)}
        disabled={isSubmitting}
        testID="create-world-cup-group-payout-top3"
      >
        <View style={styles.potSplitBarsWrap}>
          <PotBars bars={TOP3_BARS} accentColor={theme.accent} styles={styles} />
        </View>
        <View style={styles.potSplitCardBody}>
          <Text style={styles.potSplitCardName}>{CREATE_WORLD_CUP_GROUP_COPY.PAYOUT_TOP_THREE}</Text>
          <Text style={styles.potSplitCardDesc}>{CREATE_WORLD_CUP_GROUP_COPY.PAYOUT_TOP_THREE_DESC}</Text>
        </View>
        <View style={[styles.potSplitRadio, isTop3 && styles.potSplitRadioActive]}>
          {isTop3 && <View style={styles.potSplitRadioDot} />}
        </View>
      </Pressable>

      {isTop3 ? (
        <>
          <View style={styles.pctRow}>
            <View style={styles.pctField}>
              <Text style={styles.pctLabel}>{CREATE_WORLD_CUP_GROUP_COPY.PCT_FIRST}</Text>
              <TextInput value={pctFirst} onChangeText={onPctFirst} style={styles.pctInput} keyboardType="number-pad" editable={!isSubmitting} testID="create-world-cup-group-pct-1" />
            </View>
            <View style={styles.pctField}>
              <Text style={styles.pctLabel}>{CREATE_WORLD_CUP_GROUP_COPY.PCT_SECOND}</Text>
              <TextInput value={pctSecond} onChangeText={onPctSecond} style={styles.pctInput} keyboardType="number-pad" editable={!isSubmitting} testID="create-world-cup-group-pct-2" />
            </View>
            <View style={styles.pctField}>
              <Text style={styles.pctLabel}>{CREATE_WORLD_CUP_GROUP_COPY.PCT_THIRD}</Text>
              <TextInput value={pctThird} onChangeText={onPctThird} style={styles.pctInput} keyboardType="number-pad" editable={!isSubmitting} testID="create-world-cup-group-pct-3" />
            </View>
          </View>
          {showPctError ? (
            <Text style={styles.errorText}>{CREATE_WORLD_CUP_GROUP_COPY.PCT_SUM_ERROR}</Text>
          ) : null}
        </>
      ) : null}

    </View>
  );
};
